import {
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { Channel, ChannelFns, FlagOption } from 'ops/common/models/channel';
import { AlarmSetting } from 'ops/common/models/alarm-setting';
import { AlarmLevel } from 'ops/common/models/alarm-level';
import { Utils } from '@helpers/utils';

const alarmLevelToTextClassMap = new Map();
alarmLevelToTextClassMap.set(AlarmLevel.Warning, 'text-warning');
alarmLevelToTextClassMap.set(AlarmLevel.Caution, 'text-caution');
alarmLevelToTextClassMap.set(AlarmLevel.Alarm, 'text-alarm');

const alarmLevelToBorderMap = new Map();
alarmLevelToBorderMap.set(AlarmLevel.Warning, 'border-warning');
alarmLevelToBorderMap.set(AlarmLevel.Caution, 'border-caution');
alarmLevelToBorderMap.set(AlarmLevel.Alarm, 'border-alarm');

const alarmLevelToBarColorMap = new Map();
alarmLevelToBarColorMap.set(AlarmLevel.Warning, 'bar-warning');
alarmLevelToBarColorMap.set(AlarmLevel.Caution, 'bar-caution');
alarmLevelToBarColorMap.set(AlarmLevel.Alarm, 'bar-alarm');

@Component({
    selector: 'app-channel-alarm-range',
    templateUrl: './channel-alarm-range.component.html',
    styleUrls: ['./channel-alarm-range.component.scss'],
    standalone: false,
})
export class ChannelAlarmRangeComponent implements OnChanges, OnInit {
    States = FlagOption;

    @Input() channel: Channel;
    @Input() value: number = 0;
    @Input() status: FlagOption;

    @ViewChild('pointer', { static: true }) pointer: ElementRef;
    @ViewChild('valueContainer', { static: true }) valueContainer: ElementRef;
    @ViewChild('valueText', { static: true }) valueText: ElementRef;
    @ViewChild('rangeContainer', { static: true }) rangeContainer: ElementRef;

    @ViewChild('veryLow', { static: true }) veryLow: ElementRef;
    @ViewChild('low', { static: true }) low: ElementRef;
    @ViewChild('lowToNormalGap', { static: true }) lowToNormalGap: ElementRef;
    @ViewChild('normal', { static: true }) normal: ElementRef;
    @ViewChild('normalToHighGap', { static: true }) normalToHighGap: ElementRef;
    @ViewChild('high', { static: true }) high: ElementRef;
    @ViewChild('veryHigh', { static: true }) veryHigh: ElementRef;

    alarmTextClass = 'text-gap';
    alarmBorderClass = 'border-gap';

    viewInit = false;
    valueChangeDirection = 0;

    alarmStateToTextClassMap = new Map();
    alarmStateToBorderMap = new Map();
    barColorMap = new Map();

    constructor() {}

    ngOnInit(): void {
        this.initializeAlarmDisplays();
        this.updateDisplayToLatestValue();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.channel) {
            return;
        }

        if (
            changes.value &&
            !Utils.isNullOrUndefined(changes.value.previousValue) &&
            !Utils.isNullOrUndefined(changes.value.currentValue)
        ) {
            if (changes.value.previousValue > changes.value.currentValue) {
                this.valueChangeDirection = -1;
            } else if (changes.value.previousValue < changes.value.currentValue) {
                this.valueChangeDirection = 1;
            } else {
                this.valueChangeDirection = 0;
            }
        } else {
            this.valueChangeDirection = 0;
        }

        if (changes.channel) {
            this.initializeAlarmDisplays();
        }

        this.updateDisplayToLatestValue();
    }

    updateDisplayToLatestValue() {
        this.valueText.nativeElement.innerHTML = ChannelFns.scaleValue(this.channel, this.value);
        this.movePointer();
        this.updateStyles(this.status);
    }

    movePointer() {
        const percentageLeft =
            (this.value - this.channel.fcuData.measurableRange.min) /
            (this.channel.fcuData.measurableRange.max - this.channel.fcuData.measurableRange.min ||
                1);
        const elementWidth = this.rangeContainer.nativeElement.offsetWidth;
        const pointerLeft =
            elementWidth * percentageLeft - Math.round(this.pointer.nativeElement.offsetWidth / 2);
        const valueContainerOffset = this.valueContainer.nativeElement.offsetWidth / 2;
        let valueContainerLeft = elementWidth * percentageLeft - valueContainerOffset;

        //Clamp the value container within the bounds of this object
        valueContainerLeft = Math.max(valueContainerLeft, 0);
        valueContainerLeft = Math.min(
            valueContainerLeft,
            elementWidth - this.valueContainer.nativeElement.offsetWidth,
        );
        this.pointer.nativeElement.style.left = pointerLeft + 'px';
        if (this.valueChangeDirection == 0) {
            let extra = Math.abs(pointerLeft - valueContainerLeft) / 2;
            this.valueContainer.nativeElement.style.left = valueContainerLeft + extra + 'px';
        } else {
            this.valueContainer.nativeElement.style.left = valueContainerLeft + 'px';
        }
    }

    updateStyles(alarmState) {
        this.alarmTextClass = this.alarmStateToTextClassMap.get(alarmState);
        this.alarmBorderClass = this.alarmStateToBorderMap.get(alarmState);
    }

    applyFlex() {
        const ranges = this.calculateRanges();
        this.assignFlex(this.veryLow, ranges.veryLow);
        this.assignFlex(this.low, ranges.low);
        this.assignFlex(this.lowToNormalGap, ranges.lowToNormalGap);
        this.assignFlex(this.normal, ranges.normal);
        this.assignFlex(this.normalToHighGap, ranges.normalToHighGap);
        this.assignFlex(this.high, ranges.high);
        this.assignFlex(this.veryHigh, ranges.veryHigh);
    }

    assignFlex(element: ElementRef, range: { min: number; max: number }) {
        element.nativeElement.style.flex = '' + (range.max - range.min);
    }

    calculateRanges() {
        const fcuAlarms = this.channel.fcuData;
        const normal = this.toRange(fcuAlarms.normalRange.min, fcuAlarms.normalRange.max);
        const veryHigh = this.toRange(
            fcuAlarms.alarmSettingVeryHigh.setPoint,
            fcuAlarms.measurableRange.max,
            !fcuAlarms.alarmSettingVeryHigh.enable,
        );
        const high = this.toRange(
            fcuAlarms.alarmSettingHigh.setPoint,
            veryHigh.min || fcuAlarms.measurableRange.max,
            !fcuAlarms.alarmSettingHigh.enable,
        );
        const normalToHighGap = this.toRange(
            normal.max,
            high.min || veryHigh.min || fcuAlarms.measurableRange.max,
        );
        const veryLow = this.toRange(
            fcuAlarms.measurableRange.min,
            fcuAlarms.alarmSettingVeryLow.setPoint,
            !fcuAlarms.alarmSettingVeryLow.enable,
        );
        const low = this.toRange(
            veryLow.max || fcuAlarms.measurableRange.min,
            fcuAlarms.alarmSettingLow.setPoint,
            !fcuAlarms.alarmSettingLow.enable,
        );
        const lowToNormalGap = this.toRange(
            low.max || veryLow.max || fcuAlarms.measurableRange.min,
            normal.min,
        );

        return {
            veryLow,
            low,
            lowToNormalGap,
            normal,
            normalToHighGap,
            high,
            veryHigh,
        };
    }

    getRange(alarmValue: AlarmSetting) {
        return alarmValue.enable ? alarmValue.setPoint : 0;
    }

    toRange(min: number, max: number, zeroOut: boolean = false): { min: number; max: number } {
        return zeroOut ? { min: 0, max: 0 } : { min, max };
    }

    shouldShowLeftDivider(): boolean {
        return (
            this.channel.fcuData.alarmSettingLow.enable &&
            this.channel.fcuData.alarmSettingVeryLow.enable
        );
    }

    shouldShowRightDivider(): boolean {
        return (
            this.channel.fcuData.alarmSettingHigh.enable &&
            this.channel.fcuData.alarmSettingVeryHigh.enable
        );
    }

    initializeAlarmDisplays() {
        this.alarmStateToTextClassMap.set(
            FlagOption.LOW_LOW,
            alarmLevelToTextClassMap.get(this.channel.fcuData.alarmSettingVeryLow.alarmLevel),
        );
        this.alarmStateToTextClassMap.set(
            FlagOption.LOW,
            alarmLevelToTextClassMap.get(this.channel.fcuData.alarmSettingLow.alarmLevel),
        );
        this.alarmStateToTextClassMap.set(
            FlagOption.FEEDBACK,
            alarmLevelToTextClassMap.get(this.channel.fcuData.alarmSettingFeedbackError.alarmLevel),
        );
        this.alarmStateToTextClassMap.set(FlagOption.NORMAL, 'text-normal');
        this.alarmStateToTextClassMap.set(
            FlagOption.HIGH,
            alarmLevelToTextClassMap.get(this.channel.fcuData.alarmSettingHigh.alarmLevel),
        );
        this.alarmStateToTextClassMap.set(
            FlagOption.HIGH_HIGH,
            alarmLevelToTextClassMap.get(this.channel.fcuData.alarmSettingVeryHigh.alarmLevel),
        );
        this.alarmStateToTextClassMap.set(FlagOption.UNDEFINED, 'text-gap');

        this.alarmStateToBorderMap.set(
            FlagOption.LOW_LOW,
            alarmLevelToBorderMap.get(this.channel.fcuData.alarmSettingVeryLow.alarmLevel),
        );
        this.alarmStateToBorderMap.set(
            FlagOption.LOW,
            alarmLevelToBorderMap.get(this.channel.fcuData.alarmSettingLow.alarmLevel),
        );
        this.alarmStateToBorderMap.set(
            FlagOption.FEEDBACK,
            alarmLevelToTextClassMap.get(this.channel.fcuData.alarmSettingFeedbackError.alarmLevel),
        );

        this.alarmStateToBorderMap.set(FlagOption.NORMAL, 'border-normal');
        this.alarmStateToBorderMap.set(
            FlagOption.HIGH,
            alarmLevelToBorderMap.get(this.channel.fcuData.alarmSettingHigh.alarmLevel),
        );
        this.alarmStateToBorderMap.set(
            FlagOption.HIGH_HIGH,
            alarmLevelToBorderMap.get(this.channel.fcuData.alarmSettingVeryHigh.alarmLevel),
        );
        this.alarmStateToBorderMap.set(FlagOption.UNDEFINED, 'border-gap');

        this.barColorMap.set(
            FlagOption.LOW_LOW,
            alarmLevelToBarColorMap.get(this.channel.fcuData.alarmSettingVeryLow.alarmLevel),
        );
        this.barColorMap.set(
            FlagOption.LOW,
            alarmLevelToBarColorMap.get(this.channel.fcuData.alarmSettingLow.alarmLevel),
        );
        this.barColorMap.set(
            FlagOption.HIGH,
            alarmLevelToBarColorMap.get(this.channel.fcuData.alarmSettingHigh.alarmLevel),
        );
        this.barColorMap.set(
            FlagOption.HIGH_HIGH,
            alarmLevelToBarColorMap.get(this.channel.fcuData.alarmSettingVeryHigh.alarmLevel),
        );

        this.applyFlex();
    }
}
